import { FC, memo, useMemo } from "react"
import WearableCardContent from "./WearableCardContent"
import WearableCardLarge from "./WearableCardLarge"
import WearableCardMedium from "./WearableCardMedium"
import WearableCardSmall from "./WearableCardSmall"
import Tooltip from "@/components/shared/Tooltip"
import { CardSize, Token } from "@/types/index"
import { isEqual } from "@/utils/lodash"
import "./index.scss"

export interface WearableCardProps {
   wearableId: string
   inmateName: string
   bookingNumber?: string | null
   className?: string
   status?: WearableCardStatus
   chargeLevel?: number | null
   chargeThreshold?: number
   bpm?: number
   oxygenLevel?: number
   awaitingVitals?: boolean
   location?: string
   lowBattery?: boolean
   isClickable?: boolean
   hasAction?: boolean
   hideButtons?: boolean
   isExpanded?: boolean
   isCollapsed?: boolean
   onExpandedChange?: (isExpanded: boolean) => void
   onClick?: () => void
   onSwapDevice?: () => void
   onViewDetails?: () => void
}

export enum WearableCardStatus {
   CHARGING = "charging",
   DISCONNECTED = "disconnected",
   UNASSIGNED = "unassigned",
}

const WearableCard: FC<WearableCardProps> = ({
   chargeThreshold = 20,
   chargeLevel,
   isExpanded = false,
   isCollapsed = false,
   onClick,
   onViewDetails,
   onExpandedChange,
   ...props
}: WearableCardProps) => {
   const size =
      (localStorage.getItem(Token.SMARTWATCH_CARD_SIZE) as CardSize) ??
      CardSize.MEDIUM

   const isLowBattery = useMemo(
      () => !!chargeLevel && chargeLevel <= chargeThreshold,
      [chargeThreshold, chargeLevel]
   )

   const hasAction = useMemo(() => isLowBattery, [isLowBattery])

   const isClickable = useMemo(() => {
      return size !== CardSize.LARGE || !!onClick
   }, [size, onClick])

   const cardLarge = (
      <WearableCardLarge
         {...props}
         hasAction={hasAction}
         isClickable={isClickable}
         lowBattery={isLowBattery}
         chargeLevel={chargeLevel}
         isExpanded={isExpanded}
         onViewDetails={onViewDetails}
         onClick={() => onExpandedChange?.(false)}
      />
   )

   if (isExpanded && size !== CardSize.LARGE) {
      return cardLarge
   }

   if (size === CardSize.MEDIUM)
      return (
         <Tooltip
            classes={{
               tooltip: "wearable-card__tooltip-container",
            }}
            content={
               <WearableCardContent
                  {...props}
                  hasAction={hasAction}
                  lowBattery={isLowBattery}
                  hideButtons
                  className="wearable-card__tooltip-content"
                  onViewDetails={onViewDetails}
               />
            }
         >
            <span className="wearable-card--medium-grid">
               <WearableCardMedium
                  {...props}
                  isClickable={isClickable}
                  hasAction={hasAction}
                  lowBattery={isLowBattery}
                  chargeLevel={chargeLevel}
                  isCollapsed={isCollapsed}
                  onClick={() => onExpandedChange?.(true)}
               />
            </span>
         </Tooltip>
      )

   if (size === CardSize.SMALL)
      return (
         <Tooltip
            classes={{
               tooltip: "wearable-card__tooltip-container",
            }}
            content={
               <WearableCardContent
                  {...props}
                  lowBattery={isLowBattery}
                  hideButtons
                  className="wearable-card__tooltip-content"
                  onViewDetails={onViewDetails}
               />
            }
         >
            <span>
               <WearableCardSmall
                  {...props}
                  isClickable={isClickable}
                  hasAction={hasAction}
                  lowBattery={isLowBattery}
                  chargeLevel={chargeLevel}
                  isCollapsed={isCollapsed}
                  onClick={() => onExpandedChange?.(true)}
               />
            </span>
         </Tooltip>
      )

   return cardLarge
}

export default memo(WearableCard, (prevProps, nextProps) => {
   const keys: (keyof WearableCardProps)[] = [
      "wearableId",
      "inmateName",
      "bookingNumber",
      "status",
      "chargeLevel",
      "bpm",
      "oxygenLevel",
      "awaitingVitals",
      "location",
      "isExpanded",
      "isCollapsed",
   ]
   return keys.every((key) => isEqual(prevProps[key], nextProps[key]))
})
